import React, { useMemo, useRef, useEffect } from "react";
import WrapperContainer from "@components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import PageTopSection from "@src/components/PageTopSection";
import {
  TopBarSection,
  TopBarColumn,
} from "@src/components/PageTopSection/PageTopSection";
import PageTitle from "@src/components/PageTitle";
import { theme } from "@src/theme";
import styled from "styled-components";
import { UIButton } from "@src/components/UIButton";
import { Link, useNavigate } from "react-router-dom";
import { useInfiniteArticles } from "@src/queries/articleQueries";
import { useAuthCurator, useRole } from "@src/hooks/useAuth";
import TableComponent from "@src/components/TableComponent/TableComponent";
import CellWithTooltip from "@src/components/CellWithTooltip";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import { IArticle } from "@src/apis/articleApis";
import TableActionButton, {
  TableActionButtonContainer,
} from "@src/components/TableComponent/TableActionButton";
import EditIcon from "@src/assets/img/edit-icon.svg";
import TrashIcon from "@src/assets/img/trash-icon.svg";
import dayjs from "dayjs";
import { buildMediaUrl } from "@src/utils/mediaUrl";

const DescriptionText = styled.p`
  color: ${({ theme }): string => theme.colors.white};
  font-size: 14px;
  margin: 0;
  margin-left: ${({ theme }): string => theme.margins.medium};
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  max-width: 390px;
  gap: ${({ theme }): string => theme.margins.medium};
  margin-top: ${({ theme }): string => theme.margins.small};
`;

const StatusBadge = styled.span<{
  $status: "published" | "draft" | "submitted";
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  min-width: 110px;
  width: 110px;
  font-weight: bold;
  font-family: ${({ theme }): string => theme.fonts.body};
  border: 1px solid
    ${({ $status, theme }) =>
      $status === "published"
        ? theme.colors.yellow
        : $status === "submitted"
          ? theme.colors.yellow
          : theme.colors.darkGray};
  background-color: ${({ $status, theme }) =>
    $status === "published"
      ? theme.colors.yellow
      : $status === "submitted"
        ? theme.colors.opacity_yellow
        : theme.colors.darkGray};
  color: ${({ $status, theme }) =>
    $status === "published"
      ? theme.colors.black
      : $status === "submitted"
        ? theme.colors.yellow
        : theme.colors.gray100};
  text-transform: capitalize;
`;

const DateCell = styled.div`
  color: ${({ theme }): string => theme.colors.white};
  font-size: 14px;
  white-space: pre-line;
  text-align: center;
`;

const ArticlesListPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const curator = useAuthCurator();
  const role = useRole();
  const tableEndRef = useRef<HTMLTableRowElement | null>(null);
  const isAdminOrManager = role === "admin" || role === "manager";

  const {
    data: articlesData,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteArticles(
    {
      curatorId: isAdminOrManager ? undefined : curator?.documentId || "",
      pageSize: 25,
    },
    {
      enabled: isAdminOrManager || !!curator?.documentId,
    }
  );

  const articles = useMemo(() => {
    if (!articlesData?.pages) return [];
    const allArticles: IArticle[] = [];
    articlesData.pages.forEach((page) => {
      const response = page as AxiosResponse<ApiResponse<IArticle[]>>;
      if (response?.data?.data) {
        allArticles.push(...response.data.data);
      }
    });
    return allArticles;
  }, [articlesData]);

  useEffect(() => {
    if (!tableEndRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
          observer.unobserve(target.target);
          fetchNextPage().finally(() => {
            if (hasNextPage) observer.observe(target.target);
          });
        }
      },
      { root: null, rootMargin: "200px", threshold: 0.1 }
    );

    const currentRef = tableEndRef.current;
    observer.observe(currentRef);

    return () => {
      observer.unobserve(currentRef);
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const formatArticleDate = (date: string | undefined): string => {
    if (!date) return "";
    const formattedTime = dayjs(date).format("HH:mm");
    const formattedDate = dayjs(date).format("YYYY/MM/DD");
    return `${formattedTime}\n${formattedDate}`;
  };

  const columns = useMemo(
    () => [
      {
        Header: "Title",
        accessor: "header",
        enableSort: true,
        Cell: ({ value }: { value: string }): React.JSX.Element => (
          <CellWithTooltip value={value || ""} />
        ),
      },
      {
        Header: "Photo",
        accessor: "featuredImage",
        width: "80px",
        Cell: ({
          value,
        }: {
          value: { url?: string; formats?: any } | null | undefined;
        }): React.JSX.Element => {
          const imageUrl = buildMediaUrl(
            value?.formats?.thumbnail?.url || value?.url || ""
          );
          return imageUrl ? (
            <img
              src={imageUrl}
              alt="Article"
              style={{
                width: "50px",
                height: "50px",
                objectFit: "cover",
                borderRadius: "4px",
              }}
            />
          ) : (
            <div
              style={{
                width: "50px",
                height: "50px",
                backgroundColor: theme.colors.gray400,
                borderRadius: "4px",
              }}
            />
          );
        },
      },
      {
        Header: "Related Playlists",
        accessor: "playlist",
        Cell: ({
          value,
        }: {
          value: { title?: string; documentId?: string } | null | undefined;
        }): React.JSX.Element => (
          <span style={{ color: theme.colors.white }}>
            {value?.title || "-"}
          </span>
        ),
      },
      {
        Header: "Curator",
        accessor: "curator",
        Cell: ({
          value,
        }: {
          value: { name?: string; documentId?: string } | null | undefined;
        }): React.JSX.Element => (
          <CellWithTooltip
            value={value?.name || "-"}
            tooltipOptions={{ content: value?.name || "" }}
          />
        ),
      },
      {
        Header: "Date",
        accessor: "updatedAt",
        enableSort: true,
        centerContent: true,
        Cell: ({ value }: { value: string }): React.JSX.Element => (
          <DateCell>{formatArticleDate(value)}</DateCell>
        ),
      },
      {
        Header: "Status",
        accessor: "articleStatus",
        enableSort: true,
        centerContent: true,
        Cell: ({ value }: { value: string }): React.JSX.Element => {
          const raw = (value || "draft").toLowerCase();
          const normalized: "published" | "draft" | "submitted" =
            raw === "published"
              ? "published"
              : raw === "submitted"
                ? "submitted"
                : "draft";
          return (
            <StatusBadge $status={normalized}>{value || "draft"}</StatusBadge>
          );
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        width: "100px",
        centerContent: true,
        Cell: ({ row }: { row: IArticle }): React.JSX.Element => (
          <TableActionButtonContainer>
            <TableActionButton
              $tooltipContent="Edit Article"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/write-article/${row.documentId}`);
              }}
              alt="edit"
              iconSrc={EditIcon}
            />
            <TableActionButton
              $tooltipContent="Delete Article"
              onClick={(e) => {
                e.stopPropagation();
              }}
              alt="delete"
              iconSrc={TrashIcon}
            />
          </TableActionButtonContainer>
        ),
      },
    ],
    [navigate]
  );

  const handleRowClick = (row: any): void => {
    navigate(`/write-article/${row.documentId}`);
  };

  return (
    <WrapperContainer>
      <PageTopSection>
        <TopBarSection columnCount={3}>
          <TopBarColumn>
            <PageTitle>Curator Articles</PageTitle>
            <ButtonContainer>
              <UIButton
                onClick={() => navigate("/write-article")}
                variant="primaryOutline"
                style={{ minWidth: 90, minHeight: 30, fontWeight: "bold" }}
              >
                Write
              </UIButton>
              <DescriptionText>
                Write and publish articles to showcase on your Curator Page.
              </DescriptionText>
            </ButtonContainer>
          </TopBarColumn>
          <TopBarColumn></TopBarColumn>
        </TopBarSection>
      </PageTopSection>
      <ContentContainer>
        {isLoading && articles.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: `calc(100vh - 234px)`,
            }}
          >
            <span
              style={{
                color: theme.colors.gray,
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              Loading articles...
            </span>
          </div>
        ) : articles.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: `calc(100vh - 234px)`,
            }}
          >
            <span
              style={{
                color: theme.colors.gray,
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              You don't have any article yet.
            </span>

            <span
              style={{
                color: theme.colors.gray,
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              Why not start{" "}
              <Link to="/write-article">
                <span
                  style={{
                    color: theme.colors.gray,
                    textDecoration: "underline",
                  }}
                >
                  writing one?
                </span>
              </Link>
            </span>
          </div>
        ) : (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TableComponent
              columns={columns}
              data={articles}
              onRowClick={handleRowClick}
              loaderRef={tableEndRef}
              variant="darkBg"
            />
            {isFetchingNextPage && (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  padding: theme.margins.medium,
                }}
              >
                <span style={{ color: theme.colors.gray }}>
                  Loading more...
                </span>
              </div>
            )}
          </div>
        )}
      </ContentContainer>
    </WrapperContainer>
  );
};

export default ArticlesListPage;
